// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import { FeaturedGames } from './landing/FeaturedGames';
import {
  RoomActionBar,
  RoomsGrid,
  CreateRoomModal,
  JoinRoomModal,
  LoginPromptModal,
  QuickSettingsModal,
} from './rooms';
import { SessionConflictModal } from './SessionConflictModal';
import { useRoomManagement } from '../hooks/useRoomManagement';
import { api } from '../lib/api';
import { useAsyncOperation } from '../lib/useAsyncOperation';
import { validateDisplayName } from '../lib/validation/displayName';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';
import type { RoomInfo, GameSummary } from '../types/game';

interface RoomListProps {
  onRoomJoined?: () => void;
}

export function RoomList({ onRoomJoined }: RoomListProps) {
  const navigate = useNavigate();
  const {
    playerId: storedPlayerId,
    playerToken: storedPlayerToken,
    displayName: storedDisplayName,
    sessionId,
    setSession,
  } = useGameStore();
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => !!s.accessToken && !!s.user);
  const isAdmin = user?.permissions.includes('admin') ?? false;

  // Room state with async operation hook
  const [rooms, setRooms] = useState<RoomInfo[]>([]);
  const [isPulsing, setIsPulsing] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const roomsOp = useAsyncOperation<{ rooms: RoomInfo[] }>({
    onSuccess: response => {
      setRooms(response.rooms);
      setIsPulsing(true);
      setTimeout(() => setIsPulsing(false), 1000);
      setInitialLoad(false);
    },
    onError: () => setInitialLoad(false),
    fallbackMessage: 'Failed to load rooms',
  });

  // Advanced mode (global toggle)
  const [advancedMode, setAdvancedMode] = useState(() => {
    const stored = localStorage.getItem('advancedMode');
    return stored === 'true';
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showQuickSettings, setShowQuickSettings] = useState(false);
  const [quickSettingsGameId, setQuickSettingsGameId] = useState<string>('');

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [displayNameError, setDisplayNameError] = useState<string | undefined>();
  const [sessionIdInput, setSessionIdInput] = useState('');

  // Game selection
  const [games, setGames] = useState<GameSummary[]>([]);
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [defaultGame, setDefaultGame] = useState<string>('');

  // Room management (create/join/conflict)
  const {
    submitting,
    joinError,
    setJoinError,
    showConflictModal,
    existingSession,
    pendingAction,
    isLeavingSession,
    handleCreateRoom,
    handleJoinRoom,
    handleReturnToExisting,
    handleLeaveAndJoin,
    clearConflict,
  } = useRoomManagement({
    selectedGame,
    displayName,
    sessionIdInput,
    onRoomJoined,
  });

  // Validate display name on change
  const handleDisplayNameChange = (value: string) => {
    setDisplayName(value);
    // Only validate if user has started typing
    if (value.length > 0) {
      const result = validateDisplayName(value);
      setDisplayNameError(result.error);
    } else {
      setDisplayNameError(undefined);
    }
  };

  // Game selection with async operation hook
  const gamesOp = useAsyncOperation<{
    modes: GameSummary[];
  }>({
    onSuccess: response => {
      if (response.modes && response.modes.length > 0) {
        setGames(response.modes);
        const firstGameId = response.modes[0].game_id;
        setDefaultGame(firstGameId);
        setSelectedGame(firstGameId);
      } else {
        logger.warn('No games returned from API');
        setGames([]);
      }
    },
    onError: () => {
      logger.error('Failed to load games');
      setGames([]);
    },
  });

  // Helper to check if user can rejoin a room
  const canRejoinRoom = (room: RoomInfo): boolean => {
    return (
      room.is_started &&
      storedPlayerId !== null &&
      (room.player_ids?.includes(storedPlayerId) ?? false)
    );
  };

  // Load rooms using async operation hook
  const loadRooms = useCallback(() => {
    roomsOp.execute(() => api.listRooms());
  }, [roomsOp]);

  // Load games using async operation hook
  const loadGames = useCallback(() => {
    if (games.length > 0) return;
    gamesOp.execute(() => api.listGameModes());
  }, [games.length, gamesOp]);

  // Room action handlers
  const handleRejoinRoom = (room: RoomInfo) => {
    if (!storedPlayerId || !storedPlayerToken || !storedDisplayName) return;
    setSession(
      room.session_id,
      room.room_code,
      storedPlayerId,
      storedPlayerToken,
      storedDisplayName
    );
    onRoomJoined?.();
    navigate(`/lobby/${room.session_id}`);
  };

  const handleSpectatorJoin = (room: RoomInfo) => {
    navigate(`/spectate/${room.session_id}`);
  };

  const handleSmartJoin = (room: RoomInfo) => {
    if (!room.is_public) {
      setSessionIdInput('');
      setShowJoinModal(true);
      return;
    }
    if (isAuthenticated) {
      if (canRejoinRoom(room)) {
        handleRejoinRoom(room);
      } else {
        const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
        handleJoinRoom(fakeEvent, room.session_id);
      }
      return;
    }
    handleSpectatorJoin(room);
  };

  const handleCreateRoomClick = () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    loadGames();
    setShowCreateModal(true);
  };

  const handleDirectGameSelection = (gameId: string) => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    setQuickSettingsGameId(gameId);
    setShowQuickSettings(true);
  };

  const handleAdminDeleteRoom = async (sessionId: string) => {
    try {
      await api.adminDeleteSession(sessionId);
      toast.success('Room deleted successfully');
      await loadRooms();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete session');
    }
  };

  // Close other modals when session conflict is detected
  useEffect(() => {
    if (showConflictModal) {
      setShowCreateModal(false);
      setShowJoinModal(false);
      setShowQuickSettings(false);
    }
  }, [showConflictModal]);

  // Effects
  useEffect(() => {
    loadGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadRooms();
    const getPollingInterval = (): number | null => {
      if (sessionId) return null;
      if (!isAuthenticated) return 30000;
      return 10000;
    };
    const pollingInterval = getPollingInterval();
    if (pollingInterval) {
      const interval = setInterval(loadRooms, pollingInterval);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isAuthenticated]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === '^' || (e.shiftKey && e.key === '6')) {
        e.preventDefault();
        setAdvancedMode(prev => {
          const newValue = !prev;
          localStorage.setItem('advancedMode', String(newValue));
          return newValue;
        });
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  if (initialLoad && roomsOp.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="glass rounded-lg p-8 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-white border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading rooms...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <RoomActionBar roomCount={rooms.length} isPulsing={isPulsing} onRefresh={loadRooms} />

      <div className="container mx-auto px-4 py-8 max-w-6xl pb-16">
        {games.length > 0 && (
          <FeaturedGames
            games={games}
            onSelectGame={handleDirectGameSelection}
            onBrowseAll={() => alert('Game gallery coming in Phase 2!')}
          />
        )}

        {roomsOp.error && (
          <div className="glass rounded-lg p-4 mb-6 border-2 border-red-500">
            <p className="text-red-400">{roomsOp.error}</p>
          </div>
        )}

        <div className="glass rounded-lg p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-3xl" style={{ color: 'var(--color-primary)' }}>
              Active Rooms ({rooms.length})
            </h2>
          </div>
          <RoomsGrid
            rooms={rooms}
            canRejoinRoom={canRejoinRoom}
            onRejoin={handleRejoinRoom}
            onJoin={handleSmartJoin}
            onSpectate={handleSpectatorJoin}
            onDeleteRoom={handleAdminDeleteRoom}
            onCreateRoom={handleCreateRoomClick}
            advancedMode={advancedMode}
            isAdmin={isAdmin}
          />
        </div>
      </div>

      <CreateRoomModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateRoom}
        displayName={displayName}
        onDisplayNameChange={handleDisplayNameChange}
        displayNameError={displayNameError}
        games={games}
        selectedGame={selectedGame}
        defaultGame={defaultGame}
        onSelectGame={setSelectedGame}
        loadingGames={gamesOp.isLoading}
        submitting={submitting || !!displayNameError}
        isAuthenticated={isAuthenticated}
      />

      <JoinRoomModal
        isOpen={showJoinModal}
        onClose={() => {
          setShowJoinModal(false);
          setJoinError(null);
        }}
        onSubmit={handleJoinRoom}
        displayName={displayName}
        onDisplayNameChange={handleDisplayNameChange}
        displayNameError={displayNameError}
        sessionIdInput={sessionIdInput}
        onSessionIdChange={value => {
          setSessionIdInput(value);
          setJoinError(null);
        }}
        joinError={joinError}
        submitting={submitting || !!displayNameError}
        isAuthenticated={isAuthenticated}
      />

      <LoginPromptModal isOpen={showLoginPrompt} onClose={() => setShowLoginPrompt(false)} />

      <QuickSettingsModal
        isOpen={showQuickSettings}
        onClose={() => setShowQuickSettings(false)}
        onSubmit={e => {
          e.preventDefault();
          if (!quickSettingsGameId) return;
          setShowQuickSettings(false);
          handleCreateRoom(e, quickSettingsGameId);
        }}
        gameId={quickSettingsGameId}
        games={games}
        submitting={submitting}
      />

      {existingSession && (
        <SessionConflictModal
          isOpen={showConflictModal}
          onClose={clearConflict}
          existingSession={existingSession}
          targetGameName={
            pendingAction?.type === 'create' && pendingAction.gameId
              ? games.find(g => g.game_id === pendingAction.gameId)?.display_name ||
                pendingAction.gameId
              : 'another room'
          }
          onReturnToExisting={handleReturnToExisting}
          onLeaveAndJoin={handleLeaveAndJoin}
          isLeaving={isLeavingSession}
        />
      )}
    </div>
  );
}
